import { useRef, type KeyboardEvent } from 'react'

import type { AutopilotTabDef } from '../../demo/types'
import styles from './AutopilotTabs.module.css'

export type EngineTabKey = AutopilotTabDef['key']

interface AutopilotTabsProps {
  items: AutopilotTabDef[]
  value: EngineTabKey
  counts: { optimizations: number; generated: number }
  onChange: (value: EngineTabKey) => void
}

export function AutopilotTabs({ items, value, counts, onChange }: AutopilotTabsProps) {
  const tabRefs = useRef<Array<HTMLButtonElement | null>>([])
  const moveFocus = (event: KeyboardEvent<HTMLButtonElement>, index: number) => {
    let nextIndex: number | null = null
    if (event.key === 'ArrowRight') nextIndex = (index + 1) % items.length
    if (event.key === 'ArrowLeft') nextIndex = (index - 1 + items.length) % items.length
    if (event.key === 'Home') nextIndex = 0
    if (event.key === 'End') nextIndex = items.length - 1
    if (nextIndex === null) return
    event.preventDefault()
    const next = items[nextIndex]
    if (!next) return
    onChange(next.key)
    tabRefs.current[nextIndex]?.focus()
  }
  return <div aria-orientation="horizontal" className={styles.tabs} data-scope="ap-tabs" role="tablist">
    {items.map((item, index) => {
      const count = item.countFrom ? counts[item.countFrom] : 0
      const active = item.key === value
      return <button
        aria-controls={active ? `ap-panel-${item.key}` : undefined}
        aria-selected={active}
        className={`${styles.tab} ${active ? styles.active : ''}`}
        id={`ap-tab-${item.key}`}
        key={item.key}
        ref={(element) => { tabRefs.current[index] = element }}
        role="tab"
        tabIndex={active ? 0 : -1}
        type="button"
        onClick={() => onChange(item.key)}
        onKeyDown={(event) => moveFocus(event, index)}
      >
        {item.label}{count > 0 ? <span>{count}</span> : null}
      </button>
    })}
  </div>
}
